import React from 'react';
import { useColorPalette } from '../../hooks/useColorPalette';
import DynamicIcon from '../DynamicIcon';

/**
 * Componente di paginazione per DataTable
 * 
 * @param {Object} props
 * @param {Number} props.currentPage - Pagina corrente (0-based)
 * @param {Number} props.totalPages - Totale pagine
 * @param {Function} props.onPageChange - Callback quando cambia pagina
 * @param {Number} props.totalRecords - Totale records
 * @param {Number} props.pageSize - Elementi per pagina
 */
const Pagination = ({
    currentPage = 0,
    totalPages = 1,
    onPageChange,
    totalRecords = 0,
    pageSize = 10
}) => {
    const palette = useColorPalette();
    
    // Calcola l'intervallo di record visualizzati
    const startRecord = currentPage * pageSize + 1;
    const endRecord = Math.min((currentPage + 1) * pageSize, totalRecords);
    
    // Stili basati sulla palette
    const buttonStyle = {
        backgroundColor: palette.primary || '#4f46e5',
        color: palette.textOnPrimary || '#ffffff',
        hoverBg: palette.primaryHover || '#4338ca',
    };
    
    // Genera i pulsanti della paginazione
    const renderPageButtons = () => {
        const buttons = [];
        const maxVisiblePages = 7; // Aumentato il numero di pulsanti visibili
        
        // Prima pagina
        buttons.push(
            <button
                key="first"
                className="pagination-button"
                onClick={() => onPageChange(0)}
                disabled={currentPage === 0}
                style={{
                    opacity: currentPage === 0 ? 0.5 : 1,
                    cursor: currentPage === 0 ? 'not-allowed' : 'pointer'
                }}
                title="Prima pagina"
            >
                <DynamicIcon iconClass="fa-solid fa-angle-double-left" />
            </button>
        );
        
        // Pagina precedente
        buttons.push(
            <button
                key="prev"
                className="pagination-button"
                onClick={() => onPageChange(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                style={{
                    opacity: currentPage === 0 ? 0.5 : 1,
                    cursor: currentPage === 0 ? 'not-allowed' : 'pointer'
                }}
                title="Pagina precedente"
            >
                <DynamicIcon iconClass="fa-solid fa-angle-left" />
            </button>
        );
        
        // Logica migliorata per i numeri di pagina con ellipsis
        let startPage, endPage;
        
        if (totalPages <= maxVisiblePages) {
            // Caso semplice: mostra tutte le pagine
            startPage = 0;
            endPage = totalPages - 1;
        } else {
            // Caso complesso: usa ellipsis
            const middleCount = maxVisiblePages - 4; // Numero di pagine centrali (esclusi prima, ultima e 2 ellipsis)
            
            if (currentPage < middleCount + 1) {
                // Vicino all'inizio
                startPage = 0;
                endPage = middleCount + 1;
                
                // Aggiungi prima pagina e poi ellipsis e ultima pagina
                for (let i = startPage; i <= endPage; i++) {
                    buttons.push(renderPageButton(i));
                }
                
                // Ellipsis
                buttons.push(
                    <span key="ellipsis-end" className="pagination-ellipsis">...</span>
                );
                
                // Ultima pagina
                buttons.push(renderPageButton(totalPages - 1));
                
                // Aggiungi pulsanti successivo e ultimo
                addNextAndLastButtons(buttons);
                
                return buttons;
            } else if (currentPage > totalPages - (middleCount + 2)) {
                // Vicino alla fine
                startPage = totalPages - (middleCount + 2);
                endPage = totalPages - 1;
                
                // Prima pagina
                buttons.push(renderPageButton(0));
                
                // Ellipsis
                buttons.push(
                    <span key="ellipsis-start" className="pagination-ellipsis">...</span>
                );
                
                for (let i = startPage; i <= endPage; i++) {
                    buttons.push(renderPageButton(i));
                }
                
                // Aggiungi pulsanti successivo e ultimo
                addNextAndLastButtons(buttons);
                
                return buttons;
            } else {
                // Nel mezzo
                startPage = currentPage - Math.floor(middleCount / 2);
                endPage = currentPage + Math.floor(middleCount / 2);
                
                // Prima pagina
                buttons.push(renderPageButton(0));
                
                // Ellipsis all'inizio
                if (startPage > 1) {
                    buttons.push(
                        <span key="ellipsis-start" className="pagination-ellipsis">...</span>
                    );
                }
                
                for (let i = startPage; i <= endPage; i++) {
                    buttons.push(renderPageButton(i));
                }
                
                // Ellipsis alla fine
                if (endPage < totalPages - 2) {
                    buttons.push(
                        <span key="ellipsis-end" className="pagination-ellipsis">...</span>
                    );
                }
                
                // Ultima pagina
                if (endPage < totalPages - 1) {
                    buttons.push(renderPageButton(totalPages - 1));
                }
                
                // Aggiungi pulsanti successivo e ultimo
                addNextAndLastButtons(buttons);
                
                return buttons;
            }
        }
        
        // Caso semplice: mostra tutte le pagine senza ellipsis
        for (let i = startPage; i <= endPage; i++) {
            buttons.push(renderPageButton(i));
        }
        
        // Aggiungi pulsanti successivo e ultimo
        addNextAndLastButtons(buttons);
        
        return buttons;
    };
    
    // Helper per renderizzare un singolo pulsante pagina
    const renderPageButton = (pageNum) => {
        return (
            <button
                key={pageNum}
                className={`pagination-number ${pageNum === currentPage ? 'active' : ''}`}
                style={{
                    backgroundColor: pageNum === currentPage ? buttonStyle.backgroundColor : 'transparent',
                    color: pageNum === currentPage ? buttonStyle.color : 'inherit',
                }}
                onClick={() => onPageChange(pageNum)}
            >
                {pageNum + 1}
            </button>
        );
    };
    
    // Helper per aggiungere i pulsanti successivo e ultimo
    const addNextAndLastButtons = (buttons) => {
        // Pulsante pagina successiva
        buttons.push(
            <button
                key="next"
                className="pagination-button"
                onClick={() => onPageChange(Math.min(totalPages - 1, currentPage + 1))}
                disabled={currentPage === totalPages - 1}
                style={{
                    opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                    cursor: currentPage === totalPages - 1 ? 'not-allowed' : 'pointer'
                }}
                title="Pagina successiva"
            >
                <DynamicIcon iconClass="fa-solid fa-angle-right" />
            </button>
        );
        
        // Pulsante ultima pagina
        buttons.push(
            <button
                key="last"
                className="pagination-button"
                onClick={() => onPageChange(totalPages - 1)}
                disabled={currentPage === totalPages - 1}
                style={{
                    opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                    cursor: currentPage === totalPages - 1 ? 'not-allowed' : 'pointer'
                }}
                title="Ultima pagina"
            >
                <DynamicIcon iconClass="fa-solid fa-angle-double-right" />
            </button>
        );
    };

    return (
        <div className="pagination-container">
            <div className="pagination-info text-sm text-gray-600 mr-4">
                {totalRecords > 0 ? (
                    <span>
                        Mostrando {startRecord} - {endRecord} di {totalRecords} elementi
                    </span>
                ) : (
                    <span>0 elementi</span>
                )}
            </div>
            
            <div className="pagination-controls flex items-center">
                {renderPageButtons()}
            </div>
            
            <style jsx="true">{`
                .pagination-container {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    width: 100%;
                }
                
                .pagination-controls {
                    display: flex;
                    gap: 0.25rem;
                }
                
                .pagination-button, .pagination-number {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 2rem;
                    height: 2rem;
                    border-radius: 0.375rem;
                    transition: all 0.2s;
                    border: 1px solid #e5e7eb;
                }
                
                .pagination-button:hover:not(:disabled),
                .pagination-number:hover:not(.active) {
                    background-color: #f3f4f6;
                }
                
                .pagination-number.active {
                    font-weight: 600;
                }
                
                .pagination-ellipsis {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 2rem;
                    height: 2rem;
                }
                
                @media (max-width: 640px) {
                    .pagination-container {
                        flex-direction: column;
                        gap: 0.75rem;
                    }
                    
                    .pagination-info {
                        margin-right: 0;
                    }
                }
            `}</style>
        </div>
    );
};

export default Pagination;
